import isEqual from 'react-fast-compare';

import {isChemSearch} from 'chemistry/ChemSearchUtils';
import {SearchParams, SearchType, Sort, SortKey} from 'search/SearchConstants';

import {isBLASTSearch} from './blast/BLASTUtil';
import {IGNORE_STARRING_SEARCH_TYPES} from './SearchTypes';

export const DEFAULT_SORT_BY_SEARCH_TYPE: Record<SearchType, Array<Sort>> = {
  [SearchType.ANALYSES_AND_ANALYTICS_DASHBOARDS]: [{key: SortKey.SORT_MODIFIED_AT_KEY, reverse: false}],
  [SearchType.BENCHLING_APPS]: [{key: SortKey.SORT_MODIFIED_AT_KEY, reverse: false}],
  [SearchType.BIOENTITIES]: [{key: SortKey.SORT_REGISTRY_IDENTIFIER_KEY, reverse: false}],
  [SearchType.CONNECTIONS]: [{key: SortKey.SORT_MODIFIED_AT_KEY, reverse: true}],
  [SearchType.FOLDERS_AND_ITEMS]: [{key: SortKey.SORT_NAME_KEY, reverse: false}],
  [SearchType.JELLYFISH]: [{key: SortKey.SORT_NAME_KEY, reverse: false}],
  [SearchType.PROJECTS]: [{key: SortKey.SORT_MODIFIED_AT_KEY, reverse: true}],
  [SearchType.PROCESS_MANAGEMENT]: [{key: SortKey.SORT_CREATED_AT_KEY, reverse: true}],
  [SearchType.REQUEST]: [{key: SortKey.SORT_CREATED_AT_KEY, reverse: true}],
  [SearchType.REQUEST_V2_DASHBOARD]: [{key: SortKey.SORT_CREATED_AT_KEY, reverse: true}],
  [SearchType.REQUEST_V2_TYPES]: [{key: SortKey.SORT_CREATED_AT_KEY, reverse: true}],
  [SearchType.RUN]: [{key: SortKey.SORT_CREATED_AT_KEY, reverse: true}],
  [SearchType.GLOBAL]: [{key: SortKey.SORT_NAME_KEY, reverse: false}],
  [SearchType.SAMPLE]: [{key: SortKey.SORT_NAME_KEY, reverse: false}],
  [SearchType.STORABLE]: [{key: SortKey.SORT_BARCODE_KEY, reverse: false}],
  [SearchType.STUDY_ITEMS]: [{key: SortKey.SORT_MODIFIED_AT_KEY, reverse: true}],
  [SearchType.STUDIES]: [{key: SortKey.SORT_CREATED_AT_KEY, reverse: true}],
  [SearchType.TEMPLATE_COLLECTION]: [{key: SortKey.SORT_NAME_KEY, reverse: false}],
  [SearchType.WORKFLOW]: [{key: SortKey.SORT_CREATED_AT_KEY, reverse: true}],
  [SearchType.WORKLIST]: [{key: SortKey.SORT_MODIFIED_AT_KEY, reverse: true}],
  [SearchType.WORKLIST_ITEMS]: [{key: SortKey.SORT_WORKLIST_POSITION_KEY, reverse: false}],
  [SearchType.WORKSHEET]: [{key: SortKey.SORT_NAME_KEY, reverse: false}],
};

/** Determines whether a starred sort should be added for the specified listing (SearchType) and params.
 *
 * By default, listings add star sorts for any requests that don't include parameters configured from the
 * search text box. To add a new type of search parameter that should be excluded, update the conditional
 * with the required check.
 *
 * Listings can be excluded from this behavior (thus never sorting by starring) by adding the SearchType to
 * IGNORE_STARRING_SEARCH_TYPES.
 */
function shouldAddStarredSort(searchParams: SearchParams, searchType: SearchType): boolean {
  return (
    !IGNORE_STARRING_SEARCH_TYPES.includes(searchType) &&
    !searchParams.query &&
    !isChemSearch(searchParams) &&
    !isBLASTSearch(searchParams)
  );
}

export function getSortsOrDefault(searchParams: SearchParams, searchType: SearchType): Array<Sort> {
  if (searchParams.sorts.length) {
    return searchParams.sorts;
  }
  if (isChemSearch(searchParams)) {
    return [{key: SortKey.SORT_CHEMICAL_SIMILARITY, reverse: false}];
  }

  if (isBLASTSearch(searchParams)) {
    return [{key: SortKey.SORT_BLAST_E_VALUE, reverse: false}];
  }

  if (searchParams.query) {
    return [{key: SortKey.SORT_RELEVANCE_KEY, reverse: false}];
  }
  return DEFAULT_SORT_BY_SEARCH_TYPE[searchType];
}

export function finalizeListingSorts(searchParams: SearchParams, searchType: SearchType): SearchParams {
  const searchParamsWithSorts = populateSearchParamWithDefaultSort(searchParams, searchType);
  if (
    shouldAddStarredSort(searchParamsWithSorts, searchType) &&
    !searchParamsWithSorts.sorts.find((sort) => sort.key === SortKey.SORT_STARRED)
  ) {
    return {
      ...searchParamsWithSorts,
      sorts: [{key: SortKey.SORT_STARRED, reverse: false}, ...searchParamsWithSorts.sorts],
    };
  }
  return searchParamsWithSorts;
}

export function populateSearchParamWithDefaultSort(
  searchParams: SearchParams,
  searchType: SearchType
): SearchParams {
  return {
    ...searchParams,
    sorts: getSortsOrDefault(searchParams, searchType),
  };
}

export function hasNonDefaultSorts(sorts: Array<Sort>, searchType: SearchType): boolean {
  return (
    sorts.length > 0 &&
    !isEqual(
      // Starred sorts are added automatically by reducer code so filter them out - we want to treat it as
      // equal as long as the user controlled sort keys match the defaults, whether the star sort has been
      // added yet or not.
      sorts.filter((sort) => sort.key !== SortKey.SORT_STARRED),
      DEFAULT_SORT_BY_SEARCH_TYPE[searchType]
    )
  );
}
